import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Lightbulb, RefreshCw, Download, Bot, 
  AlertTriangle, CheckCircle, AlertCircle,
  Store, Package, Users, TrendingUp, Clock, 
  FileSpreadsheet, Target, Building2, BarChart3,
  IndianRupee, Boxes, MapPin, Calendar, 
  ChevronRight, Zap, ShieldAlert, ClipboardList,
  ArrowUpRight, ArrowDownRight, Activity
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AgentRecommendations = () => {
  const { user } = useAuth();
  const { languageConfig } = useLanguage();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRec, setSelectedRec] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');

  const agents = [
    { value: 'ReasonStockSense', label: 'StockSense', category: 'Inventory', icon: Package, color: 'text-purple-600' },
    { value: 'ReasonSalesIQ', label: 'SalesIQ', category: 'Revenue', icon: TrendingUp, color: 'text-green-600' },
    { value: 'ReasonCustomer360', label: 'Customer360', category: 'Customer', icon: Users, color: 'text-blue-600' },
    { value: 'ReasonStorePulse', label: 'StorePulse', category: 'Operations', icon: Store, color: 'text-orange-600' },
    { value: 'ReasonProductMix', label: 'ProductMix', category: 'Merchandise', icon: BarChart3, color: 'text-indigo-600' },
    { value: 'ReasonPromoIQ', label: 'PromoIQ', category: 'Sales', icon: Target, color: 'text-pink-600' },
    { value: 'ReasonReturnIQ', label: 'ReturnIQ', category: 'Operations', icon: Package, color: 'text-red-600' },
    { value: 'ReasonVendorLink', label: 'VendorLink', category: 'Supply Chain', icon: Building2, color: 'text-teal-600' },
    { value: 'ReasonDemandFlow', label: 'DemandFlow', category: 'Demand', icon: Activity, color: 'text-cyan-600' },
  ];

  const fetchRecommendations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page_size: 50 };
      if (selectedAgent) params.agent_name = selectedAgent;
      if (selectedPriority) params.priority = selectedPriority;
      
      const response = await axios.get(
        `${BACKEND_URL}/api/agents/recommendations/enhanced`,
        {
          params,
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setRecommendations(response.data.recommendations || []);
      if (response.data.recommendations?.length > 0 && !selectedRec) {
        setSelectedRec(response.data.recommendations[0]);
      }
    } catch (err) {
      setError('Failed to fetch recommendations');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedAgent, selectedPriority]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  // Extract financial impact from recommendation
  const extractFinancialImpact = (rec) => {
    const evidence = rec.evidence || {};
    if (evidence.stockValue) return evidence.stockValue;
    if (evidence.revenueImpact) return evidence.revenueImpact;
    if (evidence.costSavings) return evidence.costSavings;
    
    // Try to extract from impactSummary
    const match = rec.impactSummary?.match(/₹[\d,.]+ ?(Cr|L|K)?/i);
    if (match) return match[0];
    
    // Extract percentage
    const pctMatch = rec.impactSummary?.match(/\d+(\.\d+)?%/);
    if (pctMatch) return pctMatch[0];
    
    return null;
  };

  // Get short action title
  const getShortTitle = (rec) => {
    const title = rec.title || '';
    // Extract first meaningful phrase
    if (title.length <= 40) return title;
    const shortTitle = title.split(/[–\-:,]/).map(s => s.trim())[0];
    return shortTitle.length <= 50 ? shortTitle : shortTitle.substring(0, 47) + '...';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffHrs < 1) return 'Just now';
      if (diffHrs < 24) return `${diffHrs}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch {
      return 'N/A';
    }
  };

  const getAgentInfo = (agentName) => {
    return agents.find(a => a.value === agentName) || { label: agentName?.replace('Reason', ''), icon: Bot, color: 'text-slate-600' };
  };

  // Intelligent priority parsing - handles both "HIGH/MEDIUM/LOW" and descriptive text
  const parsePriority = (priority) => {
    if (!priority) return { level: 'MEDIUM', label: 'MED' };
    const p = priority.toUpperCase();
    
    // Direct match
    if (p === 'HIGH' || p === 'CRITICAL' || p === 'URGENT') return { level: 'HIGH', label: 'HIGH' };
    if (p === 'LOW' || p === 'MINOR') return { level: 'LOW', label: 'LOW' };
    if (p === 'MEDIUM' || p === 'MODERATE') return { level: 'MEDIUM', label: 'MED' };
    
    // Keyword detection for descriptive priorities
    const highKeywords = ['STOCKOUT', 'URGENT', 'CRITICAL', 'IMMEDIATE', 'PREVENT', 'RISK', 'LOSS', 'DEAD STOCK', 'CLEARANCE'];
    const lowKeywords = ['MONITOR', 'TRACK', 'OBSERVE', 'STABLE', 'MAINTAIN'];
    
    if (highKeywords.some(kw => p.includes(kw))) return { level: 'HIGH', label: 'HIGH' };
    if (lowKeywords.some(kw => p.includes(kw))) return { level: 'LOW', label: 'LOW' };
    
    return { level: 'MEDIUM', label: 'MED' };
  };

  const getPriorityStyle = (priority) => {
    const { level } = parsePriority(priority);
    if (level === 'HIGH') return { bg: 'bg-red-500', text: 'text-white', border: 'border-red-500', light: 'bg-red-50 border-red-200' };
    if (level === 'LOW') return { bg: 'bg-green-500', text: 'text-white', border: 'border-green-500', light: 'bg-green-50 border-green-200' };
    return { bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-500', light: 'bg-amber-50 border-amber-200' };
  };

  const exportEvidence = (rec) => {
    const evidence = rec.evidence || {};
    const details = evidence.details || [];
    let csvContent = '';
    
    if (details.length > 0) {
      // Agent-specific headers
      if (rec.agentName === 'ReasonStockSense') {
        csvContent = 'Store,SKU,Quantity,Value,Age (Days),Velocity,Status\n';
        details.forEach(d => {
          csvContent += `${d.storeId || d.store || ''},${d.skuId || d.sku || ''},${d.qty || d.quantity || ''},${d.value || ''},${d.ageDays || d.age || ''},${d.velocity || ''},${evidence.metric || ''}\n`;
        });
      } else if (rec.agentName === 'ReasonSalesIQ') {
        csvContent = 'Store,Revenue,Units,Contribution %,Trend\n';
        details.forEach(d => {
          csvContent += `${d.store || ''},${d.revenue || ''},${d.units || ''},${d.contribution || ''},${d.trend || ''}\n`;
        });
      } else if (rec.agentName === 'ReasonCustomer360') {
        csvContent = 'Customer ID,Segment,Risk Score,Last Purchase,Historical Value\n';
        details.forEach(d => {
          csvContent += `${d.customerId || ''},${d.segment || ''},${d.riskScore || ''},${d.lastPurchase || ''},${d.ltv || ''}\n`;
        });
      } else if (rec.agentName === 'ReasonStorePulse') {
        csvContent = 'Store,Health Score,Conversion Rate,Revenue,Status\n';
        details.forEach(d => {
          csvContent += `${d.store || ''},${d.healthScore || ''},${d.conversion || ''},${d.revenue || ''},${d.status || ''}\n`;
        });
      } else {
        const keys = Object.keys(details[0] || {});
        csvContent = keys.join(',') + '\n';
        details.forEach(d => {
          csvContent += keys.map(k => d[k] || '').join(',') + '\n';
        });
      }
    } else {
      // Generate evidence from available stores/skus/summary data
      csvContent = 'Category,Value\n';
      if (evidence.stores?.length) csvContent += `Affected Stores,"${evidence.stores.join('; ')}"\n`;
      if (evidence.skus?.length) csvContent += `Affected SKUs,"${evidence.skus.join('; ')}"\n`;
      if (evidence.stockQty) csvContent += `Stock Quantity,${evidence.stockQty}\n`;
      if (evidence.stockValue) csvContent += `Stock Value,${evidence.stockValue}\n`;
      if (evidence.metric) csvContent += `Metric,${evidence.metric}\n`;
      if (evidence.confidence) csvContent += `Confidence,${evidence.confidence}\n`;
      if (rec.rationale) csvContent += `Rationale,"${(rec.rationale || '').replace(/"/g, '""')}"\n`;
      if (rec.riskIfIgnored) csvContent += `Risk If Ignored,"${(rec.riskIfIgnored || '').replace(/"/g, '""')}"\n`;
      if (rec.impactSummary) csvContent += `Impact Summary,"${(rec.impactSummary || '').replace(/"/g, '""')}"\n`;
    }

    csvContent = `# Action: ${rec.title}\n# Agent: ${rec.agentName}\n# Priority: ${rec.priority}\n# Generated: ${rec.timestamp}\n\n` + csvContent;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${rec.agentName}_evidence_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Evidence exported successfully');
  };

  const exportAllRecommendations = () => {
    if (recommendations.length === 0) return;
    let csvContent = 'Agent,Priority,Action,Impact,Stores,SKUs,Value,Timestamp\n';
    recommendations.forEach(rec => {
      const e = rec.evidence || {};
      csvContent += [
        rec.agentName?.replace('Reason', ''),
        rec.priority,
        `"${(rec.title || '').replace(/"/g, '""')}"`,
        `"${extractFinancialImpact(rec) || ''}"`,
        e.stores?.length || 0,
        e.skus?.length || 0,
        e.stockValue || '',
        rec.timestamp
      ].join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `action_center_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('All actions exported');
  };

  // Build impact breakdown from evidence
  const getImpactBreakdown = (rec) => {
    const evidence = rec.evidence || {};
    const breakdown = [];
    
    if (evidence.stores?.length > 0) {
      breakdown.push({ label: 'Stores Affected', value: evidence.stores.length, icon: MapPin });
    }
    if (evidence.skus?.length > 0) {
      breakdown.push({ label: 'SKUs Involved', value: evidence.skus.length, icon: Boxes });
    }
    if (evidence.stockQty > 0) {
      breakdown.push({ label: 'Units', value: evidence.stockQty.toLocaleString(), icon: Package });
    }
    if (evidence.stockValue) {
      breakdown.push({ label: 'Value at Risk', value: evidence.stockValue, icon: IndianRupee });
    }
    if (evidence.metric) {
      breakdown.push({ label: 'Category', value: evidence.metric, icon: Target });
    }
    if (evidence.confidence) {
      breakdown.push({ label: 'Confidence', value: evidence.confidence, icon: Activity });
    }
    
    return breakdown;
  };

  // Generate action plan from recommendation
  const getActionPlan = (rec) => {
    const actions = [];
    
    // What to do
    if (rec.description) {
      actions.push({ step: 1, what: rec.description, who: 'Merchandising Team', when: 'Immediate', icon: Zap });
    }
    
    // Based on agent type, add specific actions
    if (rec.agentName === 'ReasonStockSense') {
      if (rec.evidence?.metric === 'DEAD_STOCK') {
        actions.push({ step: 2, what: 'Review dead stock SKUs and identify liquidation channels', who: 'Inventory Manager', when: 'This Week', icon: ClipboardList });
        actions.push({ step: 3, what: 'Execute markdown or transfer to clearance channel', who: 'Store Operations', when: 'Next 7 Days', icon: ArrowDownRight });
      } else if (rec.evidence?.metric === 'STOCKOUT_RISK') {
        actions.push({ step: 2, what: 'Place urgent replenishment orders with vendors', who: 'Procurement Team', when: 'Today', icon: ArrowUpRight });
        actions.push({ step: 3, what: 'Arrange inter-store transfers for immediate needs', who: 'Logistics', when: '24-48 Hours', icon: Store });
      }
    } else if (rec.agentName === 'ReasonSalesIQ') {
      actions.push({ step: 2, what: 'Analyze underperforming store operations', who: 'Regional Manager', when: 'This Week', icon: BarChart3 });
      actions.push({ step: 3, what: 'Implement corrective measures based on findings', who: 'Store Manager', when: 'Next 2 Weeks', icon: Target });
    } else if (rec.agentName === 'ReasonCustomer360') {
      actions.push({ step: 2, what: 'Launch targeted retention campaign', who: 'Marketing Team', when: 'This Week', icon: Users });
      actions.push({ step: 3, what: 'Monitor customer response and adjust', who: 'CRM Team', when: 'Ongoing', icon: Activity });
    }
    
    // Always add risk consequence
    if (rec.riskIfIgnored) {
      actions.push({ step: actions.length + 1, what: `If not done: ${rec.riskIfIgnored}`, who: '-', when: '-', icon: ShieldAlert, isRisk: true });
    }
    
    return actions;
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="agent-recommendations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="p-2.5 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl shadow-lg shadow-orange-200">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Business Action Center</h1>
            <p className="text-sm text-slate-500">Command center for AI-powered business decisions</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="agent-filter-dropdown"
          >
            <option value="">All Agents</option>
            {agents.map(a => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
          
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="priority-filter-dropdown"
          >
            <option value="">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <Button onClick={fetchRecommendations} variant="outline" size="sm" disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          
          <Button onClick={exportAllRecommendations} variant="outline" size="sm" disabled={recommendations.length === 0} data-testid="export-all-btn">
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 flex-shrink-0">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Main Content - Command Center Layout */}
      <div className="flex-1 flex gap-4 min-h-0">
        
        {/* LEFT PANEL: Signal List - Compact, Financial, Priority-driven */}
        <div className="w-[380px] flex-shrink-0 flex flex-col bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
          <div className="p-3 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-800">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-700 dark:text-white text-sm">Action Queue</span>
              <Badge variant="secondary" className="text-xs">{recommendations.length} items</Badge>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : recommendations.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Lightbulb className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No actions pending</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {recommendations.map((rec) => {
                  const agentInfo = getAgentInfo(rec.agentName);
                  const priorityInfo = parsePriority(rec.priority);
                  const priorityStyle = getPriorityStyle(rec.priority);
                  const impact = extractFinancialImpact(rec);
                  const AgentIcon = agentInfo.icon;
                  
                  return (
                    <div
                      key={rec.id}
                      onClick={() => setSelectedRec(rec)}
                      data-testid={`action-item-${rec.id}`}
                      className={`p-3 cursor-pointer transition-all duration-150 ${
                        selectedRec?.id === rec.id 
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-l-blue-600' 
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 border-l-4 border-l-transparent'
                      }`}
                    >
                      {/* Top Row: Agent + Priority */}
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <AgentIcon className={`h-3.5 w-3.5 ${agentInfo.color}`} />
                          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                            {agentInfo.label}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${priorityStyle.bg} ${priorityStyle.text}`}>
                          {priorityInfo.label}
                        </span>
                      </div>
                      
                      {/* Action Title - Short & Impactful */}
                      <h4 className="text-sm font-semibold text-slate-800 dark:text-white leading-snug mb-1.5" style={{ fontFamily: languageConfig?.font }}>
                        <TranslatedText text={getShortTitle(rec)} cacheKey={`rec:title:${rec.id}`} />
                      </h4>
                      
                      {/* Bottom Row: Impact + Time */}
                      <div className="flex items-center justify-between">
                        {impact ? (
                          <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            {impact.includes('₹') ? null : <IndianRupee className="h-3 w-3" />}
                            {impact}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                        <span className="text-[10px] text-slate-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(rec.timestamp)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL: Decision Room - Expanded Detail View */}
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col shadow-sm">
          {selectedRec ? (
            <>
              {/* Header Bar */}
              <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {(() => {
                        const info = getAgentInfo(selectedRec.agentName);
                        const Icon = info.icon;
                        return <Icon className={`h-4 w-4 ${info.color}`} />;
                      })()}
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        {getAgentInfo(selectedRec.agentName).label}
                      </span>
                      <ChevronRight className="h-3 w-3 text-slate-400" />
                      <span className="text-sm text-slate-500">{selectedRec.agentCategory}</span>
                    </div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">{selectedRec.title}</h2>
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg text-sm font-bold ${getPriorityStyle(selectedRec.priority).bg} ${getPriorityStyle(selectedRec.priority).text}`}>
                    {parsePriority(selectedRec.priority).label} PRIORITY
                  </div>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                
                {/* SECTION A: Business Context */}
                <section className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    <TranslatedText text="Business Context" cacheKey="rec:header:context" />
                  </h3>
                  
                  {/* What is happening */}
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                    <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">
                      <TranslatedText text="What's Happening" cacheKey="rec:header:what" />
                    </div>
                    <p className="text-slate-800 dark:text-slate-200 text-sm leading-relaxed" style={{ fontFamily: languageConfig?.font }}>
                      <TranslatedText text={selectedRec.description || selectedRec.title} cacheKey={`rec:desc:${selectedRec.id}`} />
                    </p>
                  </div>
                  
                  {/* Why it matters */}
                  {selectedRec.rationale && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                        <TranslatedText text="Why It Matters" cacheKey="rec:header:why" />
                      </div>
                      <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed" style={{ fontFamily: languageConfig?.font }}>
                        <TranslatedText text={selectedRec.rationale} cacheKey={`rec:rationale:${selectedRec.id}`} />
                      </p>
                    </div>
                  )}
                  
                  {/* Risk it creates */}
                  {selectedRec.riskIfIgnored && (
                    <div className="p-4 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-xl border border-red-200 dark:border-red-800">
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldAlert className="h-4 w-4 text-red-500" />
                        <span className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide">
                          <TranslatedText text="Risk If Not Addressed" cacheKey="rec:header:risk" />
                        </span>
                      </div>
                      <p className="text-red-700 dark:text-red-300 text-sm leading-relaxed" style={{ fontFamily: languageConfig?.font }}>
                        <TranslatedText text={selectedRec.riskIfIgnored} cacheKey={`rec:risk:${selectedRec.id}`} />
                      </p>
                    </div>
                  )}
                </section>

                {/* SECTION B: Impact Breakdown */}
                <section className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                    Impact Breakdown
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {getImpactBreakdown(selectedRec).map((item, idx) => {
                      const Icon = item.icon;
                      return (
                        <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600 text-center">
                          <Icon className="h-5 w-5 text-slate-400 mx-auto mb-1" />
                          <div className="text-lg font-bold text-slate-800 dark:text-white">{item.value}</div>
                          <div className="text-[10px] text-slate-500 uppercase tracking-wide">{item.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                {/* SECTION C: Evidence Table */}
                {selectedRec.evidence?.details?.length > 0 && (
                  <section className="space-y-3">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                      Evidence Data
                      <Badge variant="secondary" className="text-[10px] ml-auto">{selectedRec.evidence.details.length} records</Badge>
                    </h3>
                    
                    <div className="border border-slate-200 dark:border-slate-600 rounded-xl overflow-hidden">
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 dark:bg-slate-700 sticky top-0">
                            <tr>
                              {Object.keys(selectedRec.evidence.details[0] || {}).slice(0, 6).map(key => (
                                <th key={key} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                            {selectedRec.evidence.details.slice(0, 15).map((row, idx) => (
                              <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                {Object.values(row).slice(0, 6).map((val, vidx) => (
                                  <td key={vidx} className="px-3 py-2 text-slate-700 dark:text-slate-300">
                                    {val?.toString() || '-'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {selectedRec.evidence.details.length > 15 && (
                        <div className="px-3 py-2 bg-slate-50 dark:bg-slate-700 text-xs text-slate-500 text-center border-t border-slate-200 dark:border-slate-600">
                          Showing 15 of {selectedRec.evidence.details.length} records • Download for full data
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* SECTION D: Action Plan */}
                <section className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                    <TranslatedText text="Action Plan" cacheKey="rec:header:actionplan" />
                  </h3>
                  
                  <div className="space-y-2">
                    {getActionPlan(selectedRec).map((action, idx) => {
                      const Icon = action.icon;
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-xl border ${
                            action.isRisk 
                              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                              : 'bg-white dark:bg-slate-700/30 border-slate-200 dark:border-slate-600'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${
                              action.isRisk ? 'bg-red-100 dark:bg-red-800' : 'bg-slate-100 dark:bg-slate-600'
                            }`}>
                              {action.isRisk ? (
                                <ShieldAlert className="h-4 w-4 text-red-600 dark:text-red-400" />
                              ) : (
                                <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{action.step}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${action.isRisk ? 'text-red-700 dark:text-red-300' : 'text-slate-800 dark:text-white'}`} style={{ fontFamily: languageConfig?.font }}>
                                <TranslatedText text={action.what} cacheKey={`rec:action:${selectedRec.id}:${idx}`} />
                              </p>
                              {!action.isRisk && (
                                <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {action.who}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {action.when}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>

              {/* SECTION E: Footer - Download */}
              <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-400 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Generated: {new Date(selectedRec.timestamp).toLocaleString('en-IN')}
                  </div>
                  <Button 
                    onClick={() => exportEvidence(selectedRec)}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-200 dark:shadow-none"
                    disabled={!(selectedRec.evidence?.details?.length || selectedRec.evidence?.stores?.length || selectedRec.evidence?.skus?.length)}
                    data-testid="download-evidence-btn"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Download Evidence (English)
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center p-8">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                  <ChevronRight className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-1">Select an Action</h3>
                <p className="text-sm text-slate-500">Choose from the queue to view details and execute</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentRecommendations;
